<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { LayerCake, Svg, Html } from "layercake";
  import { scaleBand, scaleOrdinal } from "d3-scale";
  import { max } from "d3-array";
  import { BasicDropdown, AxisX, AxisY, Tooltip } from "@urbaninstitute/dataviz-components";
  import { ColorLegend } from "@urbaninstitute/dataviz-components/maps";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import GroupedBar from "$lib/components/Chart/GroupedBar.svelte";
  import HtmlLabels from "$lib/components/Chart/HtmlLabels.svelte";
  import { getStateName } from "$utils/states.js";
  import { FORMATTERS, FORMAT, formatNumber } from "$utils/formatters.js";
  import windowWidth from "$lib/stores/windowWidth.js";
  import {
    DEMOGRAPHIC_INDICATORS,
    DEMOGRAPHIC_INDICATORS_DROPDOWN,
    TRANSIT_MODES_DROPDOWN,
    URBANWIDE_LABEL
  } from "$utils/consts.js";

  const { parsedData, currentState } = $props();

  const MODES_DROPDOWN = TRANSIT_MODES_DROPDOWN.filter(({ value }) => value !== "intercity");

  const GROUPS = ["urbanwide", ...MODES_DROPDOWN.map(({ value }) => value)];
  const COLORS = [
    urbanColors.blue_shade_lighter,
    urbanColors.blue,
    urbanColors.blue_shade_dark,
    urbanColors.blue_shade_darkest
  ];
  const LABELS = [
    URBANWIDE_LABEL,
    ...MODES_DROPDOWN.map(({ label }) => `Near ${label.toLowerCase()}`)
  ];
  const legendScale = scaleOrdinal().domain(LABELS).range(COLORS);

  const FORMAT_MAP = {
    med_home_value: FORMAT.DOLLARS_ABBR,
    white_shr: FORMAT.PERCENT,
    med_hh_income: FORMAT.DOLLARS_ABBR,
    med_rent: FORMAT.DOLLARS_ABBR
  };

  let indicator = $state("med_home_value");
  let hoveredData = $state(null);
  let activeGroup = $state(undefined);
  let activeIndex = $state(undefined);
  let activeBarEl = $state(null);

  const currentFormat = $derived(FORMAT_MAP[indicator] || FORMAT.NUMBER);
  // const fmt = $derived(FORMATTERS[currentFormat]);
  const fmt = $derived((d) => formatNumber(d, currentFormat));

  const urbanData = $derived.by(() => {
    if (!parsedData || !currentState) return [];
    const allDetail = parsedData.getDetailDataForId(currentState);
    const rows = allDetail.filter((r) => r.date === "2022-01-01");
    return rows
      .map((r) => ({
        name: r.name_e,
        bus_all: r[`key_${indicator}_bus_all_half`],
        bus_all_15: r[`key_${indicator}_bus_all_15_half`],
        rail: r[`key_${indicator}_rail_half`],
        urbanwide: r[`key_${indicator}`],
        pop: r.pop_20_24
      }))
      .sort((a, b) => (b.pop ?? 0) - (a.pop ?? 0))
      .slice(0, 5);
  });

  const stateAvg = $derived.by(() => {
    if (!parsedData || !currentState) return null;
    const row = parsedData.getRow(currentState, "2022-01-01");
    return {
      statewide_avg: row[`key_${indicator}_urb_average`]
    };
  });

  const yMax = $derived.by(() => {
    const vals = urbanData.flatMap((d) =>
      [d.bus_all, d.bus_all_15, d.rail, d.urbanwide].filter((v) => v != null)
    );
    const refVals = stateAvg?.statewide_avg != null ? [stateAvg.statewide_avg] : [];
    const allVals = [...vals, ...refVals];
    return allVals.length ? Math.max(max(allVals) * 1.2, 0.01) : 1;
  });

  const stateName = $derived(currentState ? getStateName(currentState) : "");
  const currentLabel = $derived(DEMOGRAPHIC_INDICATORS?.[indicator] || "");
  const isSmall = $derived($windowWidth < 768);

  function handleHover(e, d, group, gi, di) {
    const modeLabel = LABELS[gi];
    const indicatorLabel = DEMOGRAPHIC_INDICATORS?.[indicator] || "";
    hoveredData = {
      name: d.name,
      label: `${indicatorLabel} ${modeLabel.toLowerCase()}`,
      mode: modeLabel.toLowerCase(),
      value: d[group]
    };
    activeGroup = gi;
    activeIndex = di;
  }

  function handleLeave() {
    hoveredData = null;
    activeGroup = undefined;
    activeIndex = undefined;
  }
</script>

{#if currentState}
  <h5 class="section-title">
    What are the demographics and housing costs in areas within a half mile of transit in {stateName}?
  </h5>
  <p class="section-description">
    Nationwide, areas near transit have lower housing costs, are more racially or ethnically
    diverse, and have lower average incomes than neighborhoods farther from transit in the same
    urban area.
  </p>
  <div class="dropdown-row">
    <BasicDropdown
      data={DEMOGRAPHIC_INDICATORS_DROPDOWN}
      bind:value={indicator}
      inlineLabel="Select indicator"
      id="section-e-indicator"
      placeholder={null}
    />
  </div>
  <p class="chart-label">
    {DEMOGRAPHIC_INDICATORS?.[indicator]} by transit access in the most populous urban areas in the state
  </p>
  <ColorLegend scale={legendScale} swatch={true} />
  {#if urbanData.length > 0}
    <div class="chart-container">
      <LayerCake
        padding={{ top: 10, right: 40, bottom: 30, left: isSmall ? 80 : 120 }}
        y="name"
        x={GROUPS[0]}
        yScale={scaleBand().paddingInner(0.2).paddingOuter(0.15)}
        xDomain={[0, yMax]}
        data={urbanData}
      >
        <Svg>
          <AxisX formatTick={(d) => fmt(d)} ticks={isSmall ? 3 : 4} gridlines />
          <GroupedBar
            groups={GROUPS}
            colors={COLORS}
            separatorEffect="stroke"
            strokeColor={urbanColors.gray_shade_lightest}
            liftOnHover={true}
            {activeGroup}
            {activeIndex}
            bind:activeEl={activeBarEl}
            onhover={handleHover}
            onleave={handleLeave}
          />
        </Svg>
        <Html pointerEvents={false}>
          <HtmlLabels axis="y" />
        </Html>
      </LayerCake>
      {#if hoveredData && activeBarEl}
        <Tooltip el={activeBarEl}>
          <h5>{hoveredData.name}</h5>
          {#if !hoveredData.value}
            {#if hoveredData.mode === "near any bus or rail transit"}
              There is no bus or rail transit.
            {:else}
              There is no {hoveredData.mode?.replace("near ", "")}.
            {/if}
          {:else}
            <p>{hoveredData.label}: {fmt(hoveredData.value)}</p>
          {/if}
        </Tooltip>
      {/if}
    </div>
  {:else}
    <p class="placeholder-text">No urban area data available for this selection.</p>
  {/if}
{:else}
  <p class="placeholder-text">Select a state to compare transit and non-transit areas.</p>
{/if}

<style>
  .section-title {
    margin-block-start: 0;
  }
  .dropdown-row {
    margin-bottom: var(--spacing-4);
  }
  .placeholder-text {
    color: var(--color-gray-shade-dark);
  }
  .chart-container {
    width: 100%;
    height: 350px;
    position: relative;
  }
</style>
